import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Logo from '../shared/Logo';
import ThemeToggle from '../shared/ThemeToggle';
import { defaultPayScales, formatCLP } from '../../data/payScales';
import './Landing.css';

export default function Landing() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preSelectedRole = searchParams.get('role') || null;
  const [activeRole, setActiveRole] = useState(preSelectedRole || 'limpieza');
  const [packageExpanded, setPackageExpanded] = useState(false);
  const [calcInputs, setCalcInputs] = useState({
    limpieza: { typeIdx: 1, perWeek: 5 },
    lavado: { perWeek: 3 },
    inspector: { perWeek: 8, includeBonuses: false },
  });

  const WEEKS_PER_MONTH = 4;

  const roles = [
    { key: 'limpieza', label: 'Limpieza', icon: '🧹' },
    { key: 'lavado', label: 'Lavado', icon: '🫧' },
    { key: 'inspector', label: 'Inspector', icon: '🔍' },
  ];

  const currentPay = defaultPayScales[activeRole];

  const monthlyEstimate = useMemo(() => {
    if (activeRole === 'limpieza') {
      const rate = currentPay.items[calcInputs.limpieza.typeIdx]?.amount || 0;
      return rate * calcInputs.limpieza.perWeek * WEEKS_PER_MONTH;
    }
    if (activeRole === 'lavado') {
      return currentPay.total.amount * calcInputs.lavado.perWeek * WEEKS_PER_MONTH;
    }
    if (activeRole === 'inspector') {
      const base = currentPay.items[0].amount * calcInputs.inspector.perWeek * WEEKS_PER_MONTH;
      const bonuses = calcInputs.inspector.includeBonuses
        ? currentPay.items[2].amount + currentPay.items[3].amount
        : 0;
      return base + bonuses;
    }
    return 0;
  }, [activeRole, calcInputs, currentPay]);

  const perWeekMax = activeRole === 'limpieza' ? 18 : 40;

  function stepPerWeek(delta) {
    setCalcInputs((s) => ({
      ...s,
      [activeRole]: {
        ...s[activeRole],
        perWeek: Math.max(0, Math.min(perWeekMax, s[activeRole].perWeek + delta)),
      },
    }));
  }

  const perWeekLabel =
    activeRole === 'lavado' ? 'Cargas' : activeRole === 'inspector' ? 'Inspecciones' : 'Servicios';

  const WORK_DAYS_PER_WEEK = 6;
  const perDay = calcInputs[activeRole].perWeek / WORK_DAYS_PER_WEEK;
  const perDayFormatted = new Intl.NumberFormat('es-CL', { maximumFractionDigits: 1 }).format(perDay);

  return (
    <div className="landing">
      {/* ── Glow background ── */}
      <div className="landing__glow" />

      {/* ── Nav ── */}
      <nav className="landing__nav">
        <Logo />
        <div className="landing__nav-right">
          <span className="ik-badge ik-badge--teal">Trabajo flexible</span>
          <ThemeToggle />
        </div>
      </nav>

      {/* ── Hero ── */}
      <section className="landing__hero">
        <div className="landing__eyebrow ik-eyebrow">
          Cleaning for superhosts
        </div>

        <h1 className="landing__headline">
          Trabaja con el<br />
          estándar más<br />
          <em>exigente</em>
        </h1>

        <div className="landing__sub">
          <p className="landing__sub-lead">Buscamos</p>
          <ul className="landing__sub-list">
            <li>Operadores de limpieza</li>
            <li>Operadores de lavado</li>
            <li>Inspectores</li>
          </ul>
          <p className="landing__sub-tagline">Trabajo real, flexible y pago justo.</p>
        </div>
      </section>

      {/* ── Video ── */}
      <section className="landing__video-section">
        <div className="landing__video-box">
          <video
            className="landing__video"
            src="/intro.mp4"
            controls
            playsInline
            preload="metadata"
            onEnded={() => navigate('/postula')}
          />
        </div>
      </section>

      {/* ── Beneficios ── */}
      <section className="landing__benefits">
        <div className="landing__benefit">
          <div className="landing__benefit-icon">💰</div>
          <div className="landing__benefit-title">Pago semanal</div>
          <div className="landing__benefit-desc">Por servicio realizado, más bonos por desempeño y volumen</div>
        </div>
        <div className="landing__benefit">
          <div className="landing__benefit-icon">🎓</div>
          <div className="landing__benefit-title">Capacitación incluida</div>
          <div className="landing__benefit-desc">Academia Impekble con estándares claros antes de empezar</div>
        </div>
        <div className="landing__benefit">
          <div className="landing__benefit-icon">⏰</div>
          <div className="landing__benefit-title">Horario flexible</div>
          <div className="landing__benefit-desc">Tú decides tu carga de trabajo y tus días</div>
        </div>
      </section>

      {/* ── Tabla de pagos ── */}
      <section className="landing__pay-section">
        <div className="ik-container ik-container--narrow">
          <h2 className="ik-section-title" style={{ textAlign: 'center' }}>
            ¿Cuánto puedes ganar?
          </h2>
          <p className="ik-section-sub" style={{ textAlign: 'center', marginBottom: '28px' }}>
            Selecciona el rol que te interesa para ver los pagos
          </p>

          {/* Role tabs */}
          <div className="landing__role-tabs">
            {roles.map((r) => (
              <button
                key={r.key}
                className={`landing__role-tab ${activeRole === r.key ? 'landing__role-tab--active' : ''}`}
                onClick={() => setActiveRole(r.key)}
              >
                <span className="landing__role-tab-icon">{r.icon}</span>
                {r.label}
              </button>
            ))}
          </div>

          {/* Pay table */}
          <div className="landing__pay-card ik-fade-in" key={activeRole}>
            <div className="landing__pay-header">
              <span style={{ fontSize: '28px' }}>{currentPay.icon}</span>
              <div>
                <div className="landing__pay-title">{currentPay.title}</div>
              </div>
            </div>

            {currentPay.total ? (
              <>
                <button
                  type="button"
                  className="landing__pay-package"
                  aria-expanded={packageExpanded}
                  onClick={() => setPackageExpanded((v) => !v)}
                >
                  <div className="landing__pay-package-label">
                    <span className="landing__pay-package-chevron" data-open={packageExpanded}>
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                        <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </span>
                    {currentPay.total.label}
                  </div>
                  <div className="landing__pay-package-amount">{formatCLP(currentPay.total.amount)}</div>
                </button>

                {packageExpanded && (
                  <div className="landing__pay-items ik-fade-in">
                    {currentPay.items.map((item, i) => (
                      <div className="landing__pay-row" key={i}>
                        <div className="landing__pay-concept">{item.concept}</div>
                        <div className="landing__pay-amount">
                          <span className="landing__pay-value">{formatCLP(item.amount)}</span>
                          <span className="landing__pay-unit">{item.unit}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div className="landing__pay-items">
                {currentPay.items.map((item, i) => (
                  <div className="landing__pay-row" key={i}>
                    <div className="landing__pay-concept">{item.concept}</div>
                    <div className="landing__pay-amount">
                      <span className="landing__pay-value">{formatCLP(item.amount)}</span>
                      <span className="landing__pay-unit">{item.unit}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {currentPay.notes && (
              <div className="landing__pay-notes">{currentPay.notes}</div>
            )}
          </div>

          {/* Proceso del rol (ej: lavado) */}
          {currentPay.process && (
            <div className="landing__process ik-fade-in" key={activeRole + '-process'}>
              <div className="landing__process-title">Cómo es el proceso</div>
              <ol className="landing__process-steps">
                {currentPay.process.map((step, i) => (
                  <li className="landing__process-step" key={i}>
                    <span className="landing__process-num">{i + 1}</span>
                    <div className="landing__process-text">
                      <strong>{step.title}</strong> {step.desc}
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          )}

          {/* Calculadora de ingresos */}
          <div className="landing__calc ik-fade-in" key={activeRole + '-calc'}>
            <div className="landing__calc-header">
              <div className="landing__calc-title">Calcula tu ingreso estimado</div>
              <div className="landing__calc-sub">Ajusta los valores según tu ritmo de trabajo</div>
            </div>

            <div className="landing__calc-body">
              {activeRole === 'limpieza' && (
                <div className="landing__calc-field">
                  <label className="landing__calc-label">Tipo de propiedad</label>
                  <div className="landing__calc-pills">
                    {currentPay.items.map((item, i) => (
                      <button
                        key={i}
                        type="button"
                        className={`landing__calc-pill ${calcInputs.limpieza.typeIdx === i ? 'landing__calc-pill--active' : ''}`}
                        onClick={() =>
                          setCalcInputs((s) => ({
                            ...s,
                            limpieza: { ...s.limpieza, typeIdx: i },
                          }))
                        }
                      >
                        {item.concept}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="landing__calc-field">
                <label className="landing__calc-label">{perWeekLabel} por semana</label>
                <div className="landing__calc-stepper">
                  <button
                    type="button"
                    className="landing__calc-stepper-btn"
                    onClick={() => stepPerWeek(-1)}
                    aria-label="Disminuir"
                  >
                    −
                  </button>
                  <span className="landing__calc-stepper-value">
                    {calcInputs[activeRole].perWeek}
                  </span>
                  <button
                    type="button"
                    className="landing__calc-stepper-btn"
                    onClick={() => stepPerWeek(1)}
                    aria-label="Aumentar"
                  >
                    +
                  </button>
                </div>
                <div className="landing__calc-hint">
                  Equivale a <strong>{perDayFormatted}</strong> {perWeekLabel.toLowerCase()} por día, trabajando 6 días a la semana
                </div>
              </div>

              {activeRole === 'inspector' && (
                <label className="landing__calc-toggle">
                  <input
                    type="checkbox"
                    checked={calcInputs.inspector.includeBonuses}
                    onChange={(e) =>
                      setCalcInputs((s) => ({
                        ...s,
                        inspector: { ...s.inspector, includeBonuses: e.target.checked },
                      }))
                    }
                  />
                  <span>Incluir bonos mensuales (+$130.000 si se cumplen)</span>
                </label>
              )}
            </div>

            <div className="landing__calc-result">
              <div className="landing__calc-result-label">Estimado mensual</div>
              <div className="landing__calc-result-amount">{formatCLP(monthlyEstimate)}</div>
              <div className="landing__calc-result-hint">
                Basado en {WEEKS_PER_MONTH} semanas al mes · estimación referencial
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Requisitos ── */}
      <section className="landing__requirements">
        <div className="ik-container ik-container--narrow">
          <h2 className="ik-section-title" style={{ textAlign: 'center' }}>
            Requisitos para postular
          </h2>
          <p className="ik-section-sub" style={{ textAlign: 'center', marginBottom: '28px' }}>
            Buscamos personas responsables, detallistas y comprometidas con el estándar Impekble.
          </p>

          <div className="landing__req-grid">
            <div className="landing__req-card">
              <div className="landing__req-card-title">Para todos los roles</div>
              <ul className="landing__req-list">
                <li>Mayor de 18 años</li>
                <li>Documento vigente (RUT chileno o pasaporte)</li>
                <li>Teléfono con WhatsApp activo</li>
                <li>Disponibilidad de al menos 3 días a la semana</li>
                <li>Compromiso con estándares de calidad altos</li>
              </ul>
            </div>

            <div className="landing__req-card">
              <div className="landing__req-card-title">Adicional para Lavado</div>
              <ul className="landing__req-list">
                <li>Lavadora doméstica mínimo 7 kg</li>
                <li>Secadora doméstica mínimo 7 kg</li>
                <li>Tabla grande de planchar y plancha</li>
                <li>Experiencia previa en lavandería profesional</li>
                <li className="landing__req-note">La impresora térmica la entrega Impekble</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="landing__faq">
        <div className="ik-container ik-container--narrow">
          <h2 className="ik-section-title" style={{ textAlign: 'center' }}>
            Preguntas frecuentes
          </h2>

          <div className="landing__faq-list">
            <details className="landing__faq-item">
              <summary className="landing__faq-q">¿Necesito experiencia previa?</summary>
              <div className="landing__faq-a">
                Sí. Buscamos personas con experiencia previa demostrable en el rol al
                que postulan. La Academia Impekble te capacita en nuestros estándares,
                no en el oficio desde cero.
              </div>
            </details>

            <details className="landing__faq-item">
              <summary className="landing__faq-q">¿Cómo y cuándo me pagan?</summary>
              <div className="landing__faq-a">
                El pago es semanal, por servicio realizado. A eso se suman los bonos
                mensuales por desempeño (calificación, cero reclamos) y por volumen.
                Puedes estimar cuánto podrías ganar en la calculadora de arriba.
              </div>
            </details>

            <details className="landing__faq-item">
              <summary className="landing__faq-q">¿Qué es la Academia y el examen?</summary>
              <div className="landing__faq-a">
                Si pasas la postulación, te damos acceso a la Academia: videos cortos con
                los estándares Impekble. Al terminar rindes un examen rápido. Si lo
                apruebas, coordinamos tus 10 días de prueba con un entrenador.
              </div>
            </details>

            <details className="landing__faq-item">
              <summary className="landing__faq-q">¿Puedo postular si soy extranjero?</summary>
              <div className="landing__faq-a">
                Sí. Solo necesitas tu pasaporte o RUT vigente y situación migratoria al
                día. Completa el formulario con los datos que tengas.
              </div>
            </details>
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="landing__cta-section">
        <div className="ik-container ik-container--narrow" style={{ textAlign: 'center' }}>
          <h2 className="ik-section-title">¿Listo para postular?</h2>
          <p className="ik-section-sub" style={{ marginBottom: '28px' }}>
            El proceso toma unos 15 minutos: un video corto, un formulario y una prueba rápida.
          </p>

          <button
            className="ik-btn ik-btn--primary ik-btn--lg"
            onClick={() => navigate('/postula')}
          >
            Continuar al formulario →
          </button>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="landing__footer">
        <div className="ik-container">
          <div className="landing__footer-grid">
            <div className="landing__footer-brand">
              <Logo size="md" />
            </div>

            <div className="landing__footer-col">
              <div className="landing__footer-col-title">Postular</div>
              <a href="/postula" className="landing__footer-link">Completar formulario</a>
              <a href="mailto:unete@impekble.cl" className="landing__footer-link">unete@impekble.cl</a>
            </div>

            <div className="landing__footer-col">
              <div className="landing__footer-col-title">Impekble</div>
              <a href="https://impekble.cl" target="_blank" rel="noopener noreferrer" className="landing__footer-link">Sitio web</a>
              <a href="https://instagram.com/impekble" target="_blank" rel="noopener noreferrer" className="landing__footer-link">Instagram</a>
            </div>
          </div>

          <div className="landing__footer-bottom">
            © {new Date().getFullYear()} Impekble. Todos los derechos reservados.
          </div>
        </div>
      </footer>
    </div>
  );
}
